import { Flex } from 'components/Flex';
import { useTranslation } from 'hooks/useTranslation';
import {
  FlexWrapper,
  LastChangedFooter,
  SeniorityRankingContentWrapper,
  SubHeader,
} from 'pages/Settings/pages/Organization/SeniorityRankingPage/SeniorityRankingPage.styled';
import { SettingsPageWrapper } from 'pages/Settings/SettingsPage.styled';
import { H3, P } from 'components/Typography';
import { FC, Fragment, MouseEventHandler } from 'react';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { BKJButton } from '@bookjane2/bookjane-design-library';
import { useSeniorityRankingDataContext } from 'pages/Settings/pages/Organization/SeniorityRankingPage/SeniorityRankingPage.data.context';
import { useSeniorityRankingFormContext } from 'pages/Settings/pages/Organization/SeniorityRankingPage/SeniorityRankingPage.form.context';
import { SeniorityMethodEnum } from 'types/graphql';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';

export const SeniorityRankingTabEditView: FC = () => {
  const { t } = useTranslation();

  const { data } = useSeniorityRankingDataContext();
  const { values, onChange, isSubmitting, isDisabled, onSubmit } = useSeniorityRankingFormContext();
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);

  const isLocalFormSeniorityMethodValueSameAsRemoteValue =
    `${data?.fetchCommunity?.organization?.seniorityMethod}` === values?.seniority_method;

  const isSubmitDisabled = isLocalFormSeniorityMethodValueSameAsRemoteValue || isDisabled;

  const lastChanged =
    data?.fetchCommunity?.organization?.updatedAt || data?.fetchCommunity?.organization?.createdAt;

  const updatedBy =
    data?.fetchCommunity?.organization?.lastEditedBy?.firstName ||
    data?.fetchCommunity?.organization?.lastEditedBy?.lastName
      ? `${data?.fetchCommunity?.organization?.lastEditedBy?.firstName} ${data?.fetchCommunity?.organization?.lastEditedBy?.lastName}`
      : '';

  return (
    <SeniorityRankingContentWrapper>
      <Flex margin="0 0 40px 0">
        <FlexWrapper height="100%" width="100%">
          <SettingsPageWrapper>
            <Flex flexDirection="column" gap="10px">
              <Flex padding="0 0 30px 0">
                <H3>{t('settings:SENIORITY_RANKING')}</H3>
              </Flex>
              <Flex padding="0 0 15px 0">
                <SubHeader>{t('settings:RANKING_CALCULATION_SELECTION')}</SubHeader>
              </Flex>
              <P>{t('settings:RANKING_CALCULATION_DESCRIPTION')}</P>
              <Flex flexDirection="column">
                <Flex>
                  <BKJRadioInput
                    name="seniority_method"
                    label={t('settings:RANKING_IN_YEARS_MONTH_DAYS')}
                    value={SeniorityMethodEnum.years_months_days}
                    checked={values?.seniority_method === SeniorityMethodEnum.years_months_days}
                    onChange={onChange}
                  />
                </Flex>
                <Flex>
                  <BKJRadioInput
                    name="seniority_method"
                    label={t('settings:RANKING_IN_HOURS')}
                    value={SeniorityMethodEnum.hours}
                    checked={values?.seniority_method === SeniorityMethodEnum.hours}
                    onChange={onChange}
                  />
                </Flex>
              </Flex>
              <Flex padding="10px 0 10px 0">
                <BKJButton
                  type="submit"
                  disabled={isSubmitDisabled}
                  isLoading={isSubmitting}
                  variant="PurpleSolid"
                  width="192px"
                  onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
                >
                  {t('auth:SAVE')}
                </BKJButton>
              </Flex>
              {!!lastChanged && (
                <LastChangedFooter>
                  {`${t('location_settings:LAST_CHANGED')}: ${DateTime.fromISO(
                    lastChanged,
                  ).toFormat(`LLL d, yyyy 'at' ${is24hTimeFormat ? 'HH:mm' : 'h:mm a'}`)} `}
                  {!!updatedBy && (
                    <Fragment>{`${t('location_settings:BY')} ${updatedBy}`}</Fragment>
                  )}
                </LastChangedFooter>
              )}
            </Flex>
          </SettingsPageWrapper>
        </FlexWrapper>
      </Flex>
    </SeniorityRankingContentWrapper>
  );
};
